import { LitElement, html, css, nothing } from 'lit';
import { animate } from 'motion';

// --- WEB COMPONENT (Generic Modal Logic) ---

export class RDModal extends LitElement {
  static properties = {
    open: { type: Boolean, reflect: true },
    title: { type: String },
    size: { type: String }, // 'small', 'medium', 'large', 'full'
    mode: { type: String }, // 'modal' (default), 'page'
    preventClose: { type: Boolean, attribute: 'prevent-close' },
    noHeader: { type: Boolean, attribute: 'no-header' },
    transparent: { type: Boolean },
  };

  static styles = css`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1050;
      display: none;
      align-items: center;
      justify-content: center;
      font-family: var(--font-sans-serif, sans-serif);
      --modal-bg: var(--white-color, #fff);
      --modal-text: var(--body-color, #000);
    }

    :host([open]),
    :host([animating]) {
      display: flex;
    }

    /* Backdrop */
    .backdrop {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--overlay-color);
      backdrop-filter: blur(2px);
      opacity: 0;
    }

    /* Panel Base */
    .panel {
      position: relative;
      background-color: var(--modal-bg);
      color: var(--modal-text);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      opacity: 0;
      transform: scale(0.95);
    }

    /* Sizes */
    .panel.small {
      width: 90%;
      max-width: 300px;
      border-radius: var(--border-radius);
      max-height: 90vh;
    }
    .panel.medium {
      width: 90%;
      max-width: 500px;
      border-radius: var(--border-radius);
      max-height: 90vh;
    }
    .panel.large {
      width: 90%;
      max-width: 800px;
      border-radius: var(--border-radius);
      max-height: 90vh;
    }

    .panel.full {
      width: 100%;
      height: 100%;
      max-width: none;
      max-height: none;
      border-radius: 0;
    }

    :host([mode='page']) .panel {
      width: 100%;
      height: 100%;
      max-width: none;
      border-radius: 0;
      transform: translateY(100%);
    }

    .panel.transparent {
      background-color: transparent;
      box-shadow: none;
      border: none;
    }

    header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid var(--white-dark, #eee);
      flex-shrink: 0;
    }

    .panel.transparent header {
      border-bottom: none;
    }

    h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: inherit;
    }

    .close-btn {
      background: transparent;
      border: none;
      cursor: pointer;
      color: inherit;
      opacity: 0.7;
      padding: 0.25rem;
      border-radius: var(--border-radius);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all var(--transition-speed, 0.15s);
      margin-left: auto;
    }

    .close-btn:hover {
      background-color: rgba(0, 0, 0, 0.05);
      opacity: 1;
      color: var(--danger-color, red);
    }

    .close-btn svg {
      width: 24px;
      height: 24px;
      fill: currentColor;
    }

    .body {
      padding: 0;
      overflow-y: auto;
      flex: 1;
      scrollbar-width: thin;
    }

    footer {
      padding: 1rem 1.25rem;
      border-top: 1px solid var(--white-dark, #eee);
      background-color: inherit;
      flex-shrink: 0;
    }

    .panel.transparent footer {
      border-top: none;
    }
  `;

  constructor() {
    super();
    this.open = false;
    this.title = '';
    this.size = 'medium';
    this.mode = 'modal';
    this.preventClose = false;
    this.noHeader = false;
    this.transparent = false;
    this._handleKeydown = this._handleKeydown.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this._handleKeydown);
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this._handleKeydown);
    super.disconnectedCallback();
  }

  _handleKeydown(e) {
    if (this.open && e.key === 'Escape' && !this.preventClose) {
      this.close();
    }
  }

  async updated(changedProps) {
    if (changedProps.has('open')) {
      if (this.open) {
        this._animateOpen();
      } else {
        this._animateClose();
      }
    }
  }

  async _animateOpen() {
    this.setAttribute('animating', '');

    const backdrop = this.shadowRoot.querySelector('.backdrop');
    const panel = this.shadowRoot.querySelector('.panel');

    if (!backdrop || !panel) return;

    animate(backdrop, { opacity: [0, 1] }, { duration: 0.2 });

    if (this.mode === 'page') {
      await animate(
        panel,
        {
          opacity: [1, 1],
          transform: ['translateY(100%)', 'translateY(0)'],
        },
        {
          type: 'spring',
          stiffness: 250,
          damping: 25,
        }
      );
    } else {
      await animate(
        panel,
        {
          opacity: [0, 1],
          transform: ['scale(0.95)', 'scale(1)'],
        },
        {
          type: 'spring',
          stiffness: 300,
          damping: 20,
        }
      );
    }

    this.removeAttribute('animating');
  }

  async _animateClose() {
    this.setAttribute('animating', '');

    const backdrop = this.shadowRoot.querySelector('.backdrop');
    const panel = this.shadowRoot.querySelector('.panel');

    if (!backdrop || !panel) return;

    animate(backdrop, { opacity: 0 }, { duration: 0.2 });

    if (this.mode === 'page') {
      await animate(
        panel,
        {
          transform: 'translateY(100%)',
        },
        {
          duration: 0.2,
          easing: 'ease-out',
        }
      );
    } else {
      await animate(
        panel,
        {
          opacity: 0,
          transform: 'scale(0.95)',
        },
        {
          duration: 0.15,
          easing: 'ease-out',
        }
      );
    }

    this.removeAttribute('animating');
  }

  close() {
    this.open = false;
    this.dispatchEvent(new CustomEvent('close'));
  }

  _handleBackdropClick() {
    if (!this.preventClose) {
      this.close();
    }
  }

  render() {
    const panelClasses = ['panel', this.size];
    if (this.transparent) panelClasses.push('transparent');

    return html`
      <div class="backdrop" @click="${this._handleBackdropClick}"></div>

      <div class="${panelClasses.join(' ')}" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        ${!this.noHeader
          ? html`
              <header>
                <h2 id="modal-title">${this.title}</h2>
                <button class="close-btn" @click="${this.close}" aria-label="Close modal">
                  <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                </button>
              </header>
            `
          : nothing}

        <div class="body">
          <slot></slot>
        </div>

        <footer>
          <slot name="footer"></slot>
        </footer>
      </div>
    `;
  }
}

customElements.define('rd-modal', RDModal);

window.openModal = (id) => {
  const modal = document.getElementById(id);
  if (modal) modal.open = true;
};

window.closeModal = (id) => {
  const modal = document.getElementById(id);
  if (modal) modal.open = false;
};
